import React, { useState, useEffect, useContext } from "react";
import ReactDOM from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, Globe } from "lucide-react";
import { useFooterModals } from "../contexts/FooterModalContext";
import PolicyModals from "../pages/Main/Policy";
import { getMenuLinksByLocation } from "../redux/Actions/MenuLinkActions";
import { MenuLink } from "../modules/shared/model/menu-link.interfaces";
import { getPageManagements } from "../redux/Actions/PageManagementActions";
import { PageManagement } from "../modules/shared/model/page-management.interfaces";
import { useLanguageContext } from "../contexts/LanguageContext";
import { ContentTranslationService, TranslatedContent } from "../services/contentTranslationService";
import { useTranslation } from "react-i18next";

const InfoPopup = ({ isOpen, onClose, title, content }) => {
  if (!isOpen) return null;
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <motion.div initial={{ scale: 0.95 }} animate={{ scale: 1 }} exit={{ scale: 0.95 }} className="bg-white rounded-xl max-w-lg w-full p-6 relative">
        <button onClick={onClose} className="absolute right-4 top-4 p-2 hover:bg-gray-100 rounded-full">
          <X className="h-5 w-5" />
        </button>
        <h3 className="text-xl font-semibold mb-4">{title}</h3>
        <div className="prose max-w-none">{content}</div>
      </motion.div>
    </motion.div>
  );
};

const DynamicContentModal = ({ isOpen, onClose, title, content, isLoading, isTranslated, currentLanguage }) => {
  if (!isOpen) return null;
  return (
    <motion.div 
      initial={{ opacity: 0 }} 
      animate={{ opacity: 1 }} 
      exit={{ opacity: 0 }} 
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
    >
      <motion.div 
        initial={{ scale: 0.95 }} 
        animate={{ scale: 1 }} 
        exit={{ scale: 0.95 }} 
        className="bg-white rounded-xl max-w-4xl w-full max-h-[80vh] overflow-y-auto p-6 relative"
      >
        <button onClick={onClose} className="absolute right-4 top-4 p-2 hover:bg-gray-100 rounded-full z-10">
          <X className="h-5 w-5" />
        </button>
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between mb-4 pr-8">
              <h3 className="text-xl font-semibold">{title}</h3>
              {currentLanguage && (
                <div className="flex items-center gap-2 text-sm">
                  <Globe className="h-4 w-4 text-gray-500" />
                  <span className="text-gray-600">{currentLanguage.name}</span>
                  {isTranslated && (
                    <span className="px-2 py-1 bg-green-100 text-green-700 rounded text-xs">Translated</span>
                  )}
                </div>
              )}
            </div>
            <div 
              className="prose max-w-none" 
              dangerouslySetInnerHTML={{ __html: content }}
            />
          </>
        )}
      </motion.div>
    </motion.div>
  );
};

const SharedFooter3 = () => {
  const [showHowItWorks, setShowHowItWorks] = useState(false);
  const [showHiring, setShowHiring] = useState(false);
  const [showQuotes, setShowQuotes] = useState(false);
  const [showReviews, setShowReviews] = useState(false);

  // Get modal state from context - this persists even when SharedFooter3 unmounts!
  const {
    showPrivacyModal,
    showTermsModal,
    showProfessionalTermsModal,
    setShowPrivacyModal,
    setShowTermsModal,
    setShowProfessionalTermsModal,
    isUsingProvider
  } = useFooterModals();
  
  console.log("🔵 [SharedFooter3] isUsingProvider:", isUsingProvider);

  // State for dynamic menu links
  const [menuLinks, setMenuLinks] = useState<MenuLink[]>([]);
  const [loading, setLoading] = useState(true);

  // State for dynamic content modal
  const [showDynamicContentModal, setShowDynamicContentModal] = useState(false);
  const [dynamicContent, setDynamicContent] = useState<{ title: string; content: string; isTranslated: boolean }>({ 
    title: "", 
    content: "", 
    isTranslated: false 
  });
  const [loadingDynamicContent, setLoadingDynamicContent] = useState(false);

  // Get current language from context
  const { currentLanguage } = useLanguageContext();
  const { i18n } = useTranslation();

  // Add logging for component lifecycle and navigation
  useEffect(() => {
    console.log("🔴 [SharedFooter3] Component mounted");
    console.log("🔴 [SharedFooter3] Current URL:", window.location.href);
    
    // Listen for navigation events
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      console.log("🔴🔴🔴 [SharedFooter3] PAGE REFRESH/NAVIGATION DETECTED!");
      console.log("🔴🔴🔴 [SharedFooter3] Event:", e);
      console.log("🔴🔴🔴 [SharedFooter3] Current URL:", window.location.href);
    };

    const handlePopState = (e: PopStateEvent) => {
      console.log("🔴 [SharedFooter3] History popstate event");
      console.log("🔴 [SharedFooter3] Event:", e);
      console.log("🔴 [SharedFooter3] New URL:", window.location.href);
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    window.addEventListener("popstate", handlePopState);

    return () => {
      console.log("🔴 [SharedFooter3] Component unmounting");
      window.removeEventListener("beforeunload", handleBeforeUnload);
      window.removeEventListener("popstate", handlePopState);
    };
  }, []);

  // Helper function to get language code
  const getLanguageCode = () => {
    const lang = i18n.language || currentLanguage?.initial || "en";
    // Extract language code (e.g., "it-IT" -> "it", "en-US" -> "en")
    return lang.split("-")[0].toLowerCase();
  };

  // Fetch footer menu links on component mount and when language changes
  useEffect(() => {
    const fetchFooterMenuLinks = async () => {
      try {
        setLoading(true);
        const langCode = getLanguageCode();
        
        // Use location-specific endpoint with locationId = 2 (Footer)
        const response = await getMenuLinksByLocation(
          2, 
          {
            page: 1,
            limit: 50,
            sort: JSON.stringify([{ orderBy: "order", order: "asc" }])
          },
          langCode  // Pass the current language code
        );
        setMenuLinks(response.data);
      } catch (error) {
        console.error("Failed to fetch footer menu links:", error);
        // Use default links as fallback
        setMenuLinks([]);
      } finally {
        setLoading(false);
      }
    };

    fetchFooterMenuLinks();

    // Listen for language changes in i18n
    const handleLanguageChange = () => {
      fetchFooterMenuLinks();
    };

    i18n.on("languageChanged", handleLanguageChange);

    return () => {
      i18n.off("languageChanged", handleLanguageChange);
    };
  }, [i18n.language, currentLanguage, i18n]);

  // Create navigation links from API data or use defaults
  // Recalculate when menuLinks OR language changes
  const navigationLinks = React.useMemo(() => {
    const langCode = getLanguageCode();
    
    return menuLinks.length > 0
      ? menuLinks.map(link => {
          // Get translated title if available, otherwise use default
          let title = link.title || "Untitled";
          if (langCode && langCode !== "en" && link.translatedTitle && link.translatedTitle[langCode]) {
            title = link.translatedTitle[langCode];
          }
          
          return {
            title: title,
            url: link.url || "",
            originalLink: link,
          };
        })
      : [];
  }, [menuLinks, i18n.language, currentLanguage]);

  // Function to fetch page content with translation
  const fetchPageContentByUrl = async (url: string, linkTitle: string) => {
    try {
      console.log("🟡 [SharedFooter3] fetchPageContentByUrl called");
      console.log(`🟡 [SharedFooter3] Fetching content for URL: "${url}", Title: "${linkTitle}"`);
      console.log("🟡 [SharedFooter3] Current language:", currentLanguage);
      
      console.log("🟡 [SharedFooter3] Setting loadingDynamicContent to true");
      setLoadingDynamicContent(true);
      console.log("🟡 [SharedFooter3] Setting showDynamicContentModal to true");
      setShowDynamicContentModal(true);
      
      // Detect if this is a content management type (Terms, Privacy, etc.)
      const contentType = ContentTranslationService.detectContentType(url, linkTitle);
      console.log("🟡 [SharedFooter3] Detected content type:", contentType);
      
      let translatedContent: TranslatedContent | null = null;

      if (contentType && currentLanguage) {
        console.log(`[SharedFooter] Attempting to get translation for content type ${contentType} in language ${currentLanguage.name}`);
        // Try to get translated content from content-management-translations
        translatedContent = await ContentTranslationService.getContentWithTranslation(
          contentType,
          currentLanguage.id
        );
        console.log("[SharedFooter] Translation result:", translatedContent);
      } else {
        if (!contentType) {
          console.log("[SharedFooter] No content type detected, will try page management");
        }
        if (!currentLanguage) {
          console.log("[SharedFooter] No current language selected");
        }
      }

      if (translatedContent) {
        // Found translated content
        setDynamicContent({
          title: translatedContent.title,
          content: translatedContent.content,
          isTranslated: translatedContent.isTranslated,
        });
      } else {
        // Fallback to page management
        const response = await getPageManagements({
          page: 1,
          limit: 1,
          filters: { url: url },
        });

        if (response.data && response.data.length > 0) {
          const pageData = response.data[0];
          
          // Try to get page translation if language is selected
          if (currentLanguage) {
            const pageTranslation = await ContentTranslationService.getPageWithTranslation(
              url,
              currentLanguage.id
            );
            
            if (pageTranslation) {
              setDynamicContent({
                title: pageTranslation.title,
                content: pageTranslation.content,
                isTranslated: pageTranslation.isTranslated,
              });
            } else {
              setDynamicContent({
                title: pageData.title || linkTitle,
                content: pageData.content || "<p>No content available.</p>",
                isTranslated: false,
              });
            }
          } else {
            setDynamicContent({
              title: pageData.title || linkTitle,
              content: pageData.content || "<p>No content available.</p>",
              isTranslated: false,
            });
          }
        } else {
          setDynamicContent({
            title: linkTitle,
            content: "<p>Content not found.</p>",
            isTranslated: false,
          });
        }
      }
    } catch (error) {
      console.error("Failed to fetch page content:", error);
      setDynamicContent({
        title: linkTitle,
        content: "<p>Failed to load content. Please try again later.</p>",
        isTranslated: false,
      });
    } finally {
      setLoadingDynamicContent(false);
    }
  };

  const handleLinkClick = (e, link) => {
    console.log("🔵 [SharedFooter3] handleLinkClick called");
    console.log("🔵 [SharedFooter3] Event:", e);
    console.log("🔵 [SharedFooter3] Link:", link);
    console.log("🔵 [SharedFooter3] Link title:", link.title);
    console.log("🔵 [SharedFooter3] Link URL:", link.url);
    
    // CRITICAL: Prevent ALL possible navigation
    if (e) {
      console.log("🔵 [SharedFooter3] Preventing default and stopping propagation");
      e.preventDefault();
      e.stopPropagation();
      // stopImmediatePropagation only exists on nativeEvent
      if (e.nativeEvent) {
        if (typeof e.nativeEvent.stopImmediatePropagation === "function") {
          e.nativeEvent.stopImmediatePropagation();
        }
      }
    }
    
    console.log("🔵 [SharedFooter3] After event prevention");
    
    // Check link title and show appropriate modal with translation support
    const title = link.title?.toLowerCase() || "";
    console.log("🟢 [SharedFooter3] Title (lowercase):", title);
    
    // Use PolicyModals for Terms, Privacy, and Professional Terms (now with translation support)
    // DO NOT navigate - just open modals
    if (title.includes("terms") && !title.includes("professional")) {
      console.log("🟢 [SharedFooter3] Opening Terms Modal (NOT navigating)");
      setShowTermsModal(true);
    } else if (title.includes("privacy")) {
      console.log("🟢 [SharedFooter3] Opening Privacy Modal (NOT navigating)");
      setShowPrivacyModal(true);
    } else if (title.includes("hire your professional") || title.includes("professional")) {
      console.log("🟢 [SharedFooter3] Opening Professional Terms Modal (NOT navigating)");
      setShowProfessionalTermsModal(true);
    } else {
      console.log("🟢 [SharedFooter3] Opening Dynamic Content Modal (NOT navigating)");
      // For other links, fetch and show content from page management in modal
      if (link.url) {
        console.log("🟢 [SharedFooter3] Fetching page content for URL:", link.url);
        fetchPageContentByUrl(link.url, link.title);
      } else {
        console.log("⚠️ [SharedFooter3] No URL provided for link");
      }
    }
    
    console.log("🔵 [SharedFooter3] Returning false from handleLinkClick");
    return false;
  };

  // Don't render anything while loading
  if (loading) {
    return null;
  }

  return (
    <div
      className="w-full bg-white border-t border-gray-200 shadow-sm mt-auto"
    >
      {/* Mobile Footer (< 640px) */}
      <div className="block sm:hidden w-full px-4 py-3">
        <div className="flex justify-between items-center mb-3">
          <div className="flex items-center">
            <Globe className="h-4 w-4 text-gray-500 mr-2" />
            <span className="text-xs text-gray-600">International Services</span>
          </div>
          <div className="text-xs text-gray-600 font-medium">Europe Services & Goldman Services</div>
        </div>

        {loading ? (
          <div className="grid grid-cols-3 gap-2">
            {[...Array(6)].map((_, index) => (
              <div key={index} className="h-6 bg-gray-200 animate-pulse rounded"></div>
            ))}
          </div>
        ) : (
          <>
            {/* First row - up to 3 links */}
            {navigationLinks.length > 0 && (
              <div className="grid grid-cols-3 gap-2 mb-2">
                {navigationLinks.slice(0, 3).map((link, index) => (
                  <button
                    key={index}
                    type="button"
                    onClick={(e) => {
                      console.log(`🔵 [SharedFooter3] Button clicked (Mobile row 1, index ${index})`);
                      console.log("🔵 [SharedFooter3] Button link:", link);
                      handleLinkClick(e, link);
                    }}
                    onTouchEnd={(e) => {
                      // Mobile touch handling
                      e.preventDefault();
                      console.log(`📱 [SharedFooter3] Touch end (Mobile row 1, index ${index})`);
                      handleLinkClick(e, link);
                    }}
                    data-no-navigate="true"
                    className="text-xs text-gray-600 hover:text-gray-900 py-1 px-2 rounded transition-colors text-center cursor-pointer truncate active:bg-gray-100"
                    title={link.title}
                  >
                    {link.title}
                  </button>
                ))}
              </div>
            )}

            {/* Second row - remaining links */}
            {navigationLinks.length > 3 && (
              <div className="grid grid-cols-3 gap-2">
                {navigationLinks.slice(3, 6).map((link, index) => (
                  <button
                    key={index + 3}
                    type="button"
                    onClick={(e) => {
                      console.log(`🔵 [SharedFooter3] Button clicked (Mobile row 2, index ${index + 3})`);
                      console.log("🔵 [SharedFooter3] Button link:", link);
                      handleLinkClick(e, link);
                    }}
                    onTouchEnd={(e) => {
                      // Mobile touch handling
                      e.preventDefault();
                      console.log(`📱 [SharedFooter3] Touch end (Mobile row 2, index ${index + 3})`);
                      handleLinkClick(e, link);
                    }}
                    data-no-navigate="true"
                    className="text-xs text-gray-600 hover:text-gray-900 py-1 px-2 rounded transition-colors text-center cursor-pointer truncate active:bg-gray-100"
                    title={link.title}
                  >
                    {link.title}
                  </button>
                ))}
              </div>
            )}

            {/* Third row if more than 6 links */}
            {navigationLinks.length > 6 && (
              <div className="grid grid-cols-3 gap-2 mt-2">
                {navigationLinks.slice(6, 9).map((link, index) => (
                  <button
                    key={index + 6}
                    type="button"
                    onClick={(e) => {
                      console.log(`🔵 [SharedFooter3] Button clicked (Mobile row 3, index ${index + 6})`);
                      console.log("🔵 [SharedFooter3] Button link:", link);
                      handleLinkClick(e, link);
                    }}
                    onTouchEnd={(e) => {
                      // Mobile touch handling
                      e.preventDefault();
                      console.log(`📱 [SharedFooter3] Touch end (Mobile row 3, index ${index + 6})`);
                      handleLinkClick(e, link);
                    }}
                    data-no-navigate="true"
                    className="text-xs text-gray-600 hover:text-gray-900 py-1 px-2 rounded transition-colors text-center cursor-pointer truncate active:bg-gray-100"
                    title={link.title}
                  >
                    {link.title}
                  </button>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* Tablet Footer (640px - 1024px) - Enhanced layout */}
      <div className="hidden sm:block lg:hidden w-full px-4 py-3">
        <div className="flex justify-between items-center mb-3">
          <div className="flex items-center">
            <Globe className="h-4 w-4 text-gray-500 mr-2" />
            <span className="text-xs text-gray-600">International Services</span>
          </div>
          <div className="text-xs text-gray-600 font-medium">Europe Services & Goldman Services</div>
        </div>

        {loading ? (
          <div className="grid grid-cols-4 gap-2">
            {[...Array(8)].map((_, index) => (
              <div key={index} className="h-6 bg-gray-200 animate-pulse rounded"></div>
            ))}
          </div>
        ) : (
          <>
            {/* First row - up to 4 links */}
            {navigationLinks.length > 0 && (
              <div className="grid grid-cols-4 gap-2 mb-2">
                {navigationLinks.slice(0, 4).map((link, index) => (
                  <button
                    key={index}
                    type="button"
                    onClick={(e) => {
                      console.log(`🔵 [SharedFooter3] Button clicked (Tablet row 1, index ${index})`);
                      handleLinkClick(e, link);
                    }}
                    onTouchEnd={(e) => {
                      e.preventDefault();
                      console.log(`📱 [SharedFooter3] Touch end (Tablet row 1, index ${index})`);
                      handleLinkClick(e, link);
                    }}
                    data-no-navigate="true"
                    className="text-xs text-gray-600 hover:text-gray-900 py-1 px-2 rounded transition-colors text-center cursor-pointer truncate active:bg-gray-100"
                    title={link.title}
                  >
                    {link.title}
                  </button>
                ))}
              </div>
            )}

            {/* Second row - remaining links */}
            {navigationLinks.length > 4 && (
              <div className="grid grid-cols-4 gap-2">
                {navigationLinks.slice(4, 8).map((link, index) => (
                  <button
                    key={index + 4}
                    type="button"
                    onClick={(e) => {
                      console.log(`🔵 [SharedFooter3] Button clicked (Tablet row 2, index ${index + 4})`);
                      handleLinkClick(e, link);
                    }}
                    onTouchEnd={(e) => {
                      e.preventDefault();
                      console.log(`📱 [SharedFooter3] Touch end (Tablet row 2, index ${index + 4})`);
                      handleLinkClick(e, link);
                    }}
                    data-no-navigate="true"
                    className="text-xs text-gray-600 hover:text-gray-900 py-1 px-2 rounded transition-colors text-center cursor-pointer truncate active:bg-gray-100"
                    title={link.title}
                  >
                    {link.title}
                  </button>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* Desktop Footer (≥ 1024px) */}
      <div className="hidden lg:block w-full py-3">
        <div className="max-w-screen-2xl mx-auto flex items-center justify-between px-4 lg:px-6 xl:px-8">
          <span className="flex items-center text-sm text-gray-600">
            <Globe className="h-4 w-4 text-gray-500 mr-2" />
            International Services
          </span>

          {loading ? (
            <div className="flex items-center space-x-6">
              {[...Array(6)].map((_, index) => (
                <div key={index} className="h-4 w-16 bg-gray-200 animate-pulse rounded"></div>
              ))}
            </div>
          ) : (
            <div className="flex items-center space-x-6 flex-wrap justify-center max-w-4xl">
              {navigationLinks.map((link, index) => (
                <button
                  key={index}
                  type="button"
                  onClick={(e) => {
                    console.log(`🔵 [SharedFooter3] Button clicked (Desktop, index ${index})`);
                    handleLinkClick(e, link);
                  }}
                  onTouchEnd={(e) => {
                    e.preventDefault();
                    console.log(`📱 [SharedFooter3] Touch end (Desktop, index ${index})`);
                    handleLinkClick(e, link);
                  }}
                  data-no-navigate="true"
                  className="text-sm text-gray-600 hover:text-gray-900 transition-colors cursor-pointer whitespace-nowrap active:text-blue-600"
                  title={link.title}
                >
                  {link.title}
                </button>
              ))}
            </div>
          )}

          <span className="text-sm text-gray-600 font-medium">
            Europe Services & Goldman Services
          </span>
        </div>
      </div>

      {/* Modals */}
      <AnimatePresence>
        {showHowItWorks && (
          <InfoPopup
            isOpen={showHowItWorks}
            onClose={() => setShowHowItWorks(false)}
            title="How does it work?"
            content={
              <div className="space-y-4">
                <p>Find the perfect professional for your needs:</p>
                <ul className="list-disc pl-4">
                  <li>Search for professionals in your area</li>
                  <li>Compare multiple quotes</li>
                  <li>Read verified reviews from other customers</li>
                  <li>Choose your preferred professional and get started</li>
                </ul>
              </div>
            }
          />
        )}

        {showHiring && (
          <InfoPopup
            isOpen={showHiring}
            onClose={() => setShowHiring(false)}
            title="Hire your professional"
            content={
              <div className="space-y-4">
                <p>Direct hiring process:</p>
                <ul className="list-disc pl-4">
                  <li>Browse professional profiles</li>
                  <li>View their experience and certifications</li>
                  <li>Contact them directly</li>
                  <li>Discuss your project details</li>
                  <li>Agree on terms and get started</li>
                </ul>
              </div>
            }
          />
        )}

        {showQuotes && (
          <InfoPopup
            isOpen={showQuotes}
            onClose={() => setShowQuotes(false)}
            title="Get quotes"
            content={
              <div className="space-y-4">
                <p>Receive up to 5 quotes from different professionals:</p>
                <ul className="list-disc pl-4">
                  <li>Submit your request</li>
                  <li>Get multiple quotes</li>
                  <li>Compare prices and services</li>
                  <li>Choose the best offer</li>
                  <li>Provide anonymous feedback to get better offers</li>
                </ul>
              </div>
            }
          />
        )}

        {showReviews && (
          <InfoPopup
            isOpen={showReviews}
            onClose={() => setShowReviews(false)}
            title="Reviews"
            content={
              <div className="space-y-4">
                <p>Once you&apos;ve completed work with your chosen professional, let other customers know about your experience:</p>
                <ul className="list-disc pl-4">
                  <li>Rate your experience</li>
                  <li>Share detailed feedback</li>
                  <li>Help others make informed decisions</li>
                  <li>Build trust in our community</li>
                </ul>
              </div>
            }
          />
        )}
      </AnimatePresence>

      {/* PolicyModals rendering: 
          - If using provider: modals are rendered at provider level (persist even when footer unmounts)
          - If using fallback: render modals locally using Portal */}
      {!isUsingProvider && typeof document !== "undefined" && ReactDOM.createPortal(
        <PolicyModals
          showPrivacyModal={showPrivacyModal}
          showTermsModal={showTermsModal}
          showProfessionalTermsModal={showProfessionalTermsModal}
          onClosePrivacy={() => {
            console.log("🟣 [SharedFooter3 - Fallback] Closing Privacy Modal");
            setShowPrivacyModal(false);
          }}
          onCloseTerms={() => {
            console.log("🟣 [SharedFooter3 - Fallback] Closing Terms Modal");
            setShowTermsModal(false);
          }}
          onCloseProfessionalTerms={() => {
            console.log("🟣 [SharedFooter3 - Fallback] Closing Professional Terms Modal");
            setShowProfessionalTermsModal(false);
          }}
        />,
        document.body
      )}

      {/* Dynamic Content Modal - Also in Portal */}
      {typeof document !== "undefined" && ReactDOM.createPortal(
        <DynamicContentModal
          isOpen={showDynamicContentModal}
          onClose={() => {
            console.log("🟣 [SharedFooter3] Closing Dynamic Content Modal");
            setShowDynamicContentModal(false);
            setDynamicContent({ title: "", content: "", isTranslated: false });
          }}
          title={dynamicContent.title}
          content={dynamicContent.content}
          isLoading={loadingDynamicContent}
          isTranslated={dynamicContent.isTranslated}
          currentLanguage={currentLanguage}
        />,
        document.body
      )}
    </div>
  );
};

export default SharedFooter3;